import { useSelector } from "react-redux";
import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import type { DateRange, Lodging } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  type MapMarkerComponentProps,
  Icon,
  MapMarker,
  MapMarkerType,
  useGetPriceDisplay,
} from "@tenant/ui";
import { PackageHotelMapCard } from "../HotelMapCard";
import defaultStyles from "./PackageHotelMapMarkers.module.scss";
import { PackageHotelSelectors } from "@/store/packages";

const COMPONENT_KEY = CoreHotelsComponent.HotelMapMarkers;

type HotelMapMarkerData = {
  lodging: Lodging;
  dateRange: DateRange;
  rooms?: number;
};

export const PackageHotelMapMarkers = (
  props: MapMarkerComponentProps<HotelMapMarkerData>
): JSX.Element => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();
  const { getPriceDisplay } = useGetPriceDisplay();

  // Get package pricing for all hotels
  const packageStateByLodgingId = useSelector(
    PackageHotelSelectors.getPackageStateByLodgingId
  );

  switch (props.markerType) {
    case MapMarkerType.CLUSTER: {
      return (
        <MapMarker {...props} onClick={props.onClick}>
          {t("core-hotels.map.clusterLabel", {
            count: props.cluster.properties.point_count,
          })}
        </MapMarker>
      );
    }
    case MapMarkerType.SINGLE: {
      const { isSelected, cluster } = props;
      const hotelId = cluster.properties.data.lodging.lodging.id;

      // Get package pricing for this specific hotel
      const packageState = packageStateByLodgingId?.[hotelId];
      const packagePrice = packageState?.price?.totalPrice;

      // Use package pricing for the label if available, otherwise fall back to the original label
      const priceLabel = packagePrice
        ? getPriceDisplay({ prices: packagePrice }).priceText
        : cluster.properties.label;

      return (
        <>
          <MapMarker
            {...props}
            className={block}
            onClick={() => {
              props.onClick();
              if (props.mapRef.current) {
                props.mapRef.current.panTo({
                  lat: cluster.geometry.coordinates[1],
                  lng: cluster.geometry.coordinates[0],
                });

                const panByY =
                  props.mapRef.current.getDiv()?.offsetHeight * 0.2 || 100;
                props.mapRef.current.panBy(0, panByY);
              }
            }}
          >
            {priceLabel}
          </MapMarker>
          {isSelected && (
            <div
              className={cn("hotel-card", {
                selected: true,
              })}
            >
              <PackageHotelMapCard
                hotel={cluster.properties.data.lodging}
                dateRange={cluster.properties.data.dateRange}
                rooms={cluster.properties.data.rooms}
              />
              {props.onClose != null ? (
                <button
                  className={cn("close-button")}
                  onClick={props.onClose}
                  aria-label={t("close.button")}
                >
                  <Icon iconName={IconNameEnum.close} />
                </button>
              ) : null}
            </div>
          )}
        </>
      );
    }
  }
};
